import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Output
} from '@angular/core';
import { AppTranslationService } from '@service/translation/app-translation.service';

/**
 * Componente que representa la barra superior de la aplicación.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-top-bar',
  imports: [],
  template: `
    <div class="h-16 w-full bg-blue-500 shadow text-center">
      <div class="flex h-full items-center justify-between">
        <div class="flex items-center">
          <div class="flex items-center gap-3 ml-8">
            <img
              src="assets/images/logo-blanco.svg"
              alt="logo"
              class="size-40"
            />
          </div>
        </div>

        <!-- Información del usuario -->
        <button
          type="button"
          class="mr-8 flex items-center gap-3 rounded-md px-3 py-2 text-white transition hover:bg-blue-600"
          aria-label="Abrir menú de usuario"
          (click)="toggleUserMenu()"
        >
          <div class="text-right">
            <div class="font-semibold text-sm">{{ username() }}</div>
            @if (lastAccess()) {
              <div class="text-xs opacity-90">{{ getLastConnection() }}</div>
            }
          </div>
          <i class="pi pi-user text-2xl"></i>
          <i [class]="'pi pi-' + getIcon() + ' text-sm'"></i>
        </button>
      </div>
    </div>
  `,
  styles: ``
})
export class TopBarComponent {
  /** Nombre del usuario */
  username = input.required<string>();

  /** Último acceso del usuario */
  lastAccess = input<string>();

  /** Indica si el menú de usuario está abierto. */
  userMenuOpen = input(false);

  /** Servicio de traducción de la aplicación */
  private translatorService = inject(AppTranslationService);

  /**
   * Emite evento cuando el menú de opciones cambia de estado.
   */
  @Output() menuStateChange = new EventEmitter<string>();

  /**
   * Emite evento cuando el menú de usuario cambia de estado.
   */
  @Output() userMenuStateChange = new EventEmitter<string>();

  /**
   * Construye un item con la información del usuario.
   * @returns item que representa al usuario.
   */
  getUserItem = computed(() => {
    return {
      id: this.username(),
      path: '',
      title: this.username(),
      subtitle: this.lastAccess() ? this.getLastConnection() : '',
      icon: this.getIcon()
    };
  });

  /**
   * Obtiene la última conexión del usuario.
   */
  getLastConnection = computed(
    (): string =>
      `${this.translatorService.get('security.last-access', { lastAccess: this.lastAccess() ?? '' })}`
  );

  /**
   * Obtiene el icono que se muestra en el menú de usuario.
   */
  getIcon() {
    return this.userMenuOpen() ? 'angle-up' : 'angle-down';
  }

  /**
   * Alterna el menú de usuario.
   */
  toggleUserMenu(): void {
    this.userMenuStateChange.emit(this.userMenuOpen() ? 'closed' : 'opened');
  }
}
